import { Anchor } from '@components/anchor'
import type { PolicySectionsType } from '@components/policy-sections'
import { Text } from '@components/text'
import { checkSubsection } from '@lib/utils/policy'
import { TEXT } from '@lib/utils/text'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import type { FunctionComponent, MouseEvent } from 'react'

import styles from './table-of-contents.module.scss'

export const TableOfContents: FunctionComponent<Props> = ({
    title,
    fixOnScroll = false,
    sections = [],
    variant,
}) => {
    const router = useRouter()

    if (!sections?.length) {
        return null
    }

    const handleLinkClick = (e: MouseEvent, anchor = '') => {
        e.preventDefault()
        document.querySelector(`#${anchor}`)?.scrollIntoView({
            behavior: 'smooth',
        })

        router.push(
            {
                hash: anchor,
            },
            undefined,
            { scroll: false },
        )
    }

    const sectionKeys = sections.map((section) => {
        const isSubsection = checkSubsection(section?.title ?? '')

        // if subsection, remove the section prefix (E.g. IV.A Section title) by space, but keep all the other parts of the array
        // e.g. ['IV.A', 'Section', 'title'] -> ['Section', 'title']
        const title = isSubsection
            ? section?.title?.split(' ')?.slice(1)?.join(' ')
            : section?.title

        return (
            <Text
                key={section.anchor}
                tag='div'
                typeStyle='body-lg'
                className={classNames(
                    styles['list-item'],
                    isSubsection && styles['list-item--subsection'],
                )}
            >
                <Anchor
                    href={`#${section.anchor}`}
                    onClick={(e) => handleLinkClick(e, section.anchor)}
                >
                    {title}
                </Anchor>
            </Text>
        )
    })

    return (
        <div
            className={classNames(
                styles['table-of-contents'],
                styles[`table-of-contents--${variant}`],
                fixOnScroll && styles['table-of-contents--fixed-on-scroll'],
            )}
        >
            <Text
                tag='div'
                typeStyle={{
                    _: 'body-lg',
                }}
                bold
            >
                {title ? `${title} - ` : null}
                {TEXT.tableOfContents.title}
            </Text>
            <div>{sectionKeys}</div>
        </div>
    )
}

export default TableOfContents

interface Props {
    title?: string
    sections?: PolicySectionsType
    fixOnScroll?: boolean
    variant?: 'default' | 'sidebar'
}
